import { useEffect, useState } from "react";
import { dashboardApi } from "../api";
import { useT } from "../i18n";
import { useServiceText } from "../service-text";
import Explain from "./Explain";
import type { Insights } from "../types";
import { formatCredits, monthLabel } from "../utils";

/**
 * Am I on track, and what can I still get?
 *
 * Three questions a retainer client asks that a balance alone cannot answer:
 * how does this month compare, where does this end up, and what does the rest buy.
 *
 * Every projection here is stated WITH what it was based on. A confident number
 * from one month of data is how a client plans against a figure nobody stood
 * behind, so when there is nothing to project from, this says so rather than
 * printing a zero and letting it read as a finding.
 */
export default function OnTrack({ month }: { month: string }) {
  const [data, setData] = useState<Insights | null>(null);
  const [failed, setFailed] = useState(false);
  const t = useT();
  const st = useServiceText();

  useEffect(() => {
    let live = true;
    setData(null);
    setFailed(false);
    dashboardApi.insights(month)
      .then((result) => { if (live) setData(result); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [month]);

  // A panel that cannot load must not render as "nothing is happening".
  if (failed || !data) return null;

  const change = data.changePercent;
  const direction = change === null ? null : change > 0 ? "up" : change < 0 ? "down" : "flat";

  return (
    <section className="on-track">
      <div className="on-track-head">
        <span className="section-eyebrow">{t("pace.eyebrow")}</span>
        <h3>{t("pace.title")}</h3>
      </div>

      <div className="on-track-grid">
        <article className="on-track-card">
          <span>{t("pace.thisVsLast")} <Explain of="thisVsLast" /></span>
          <strong>{formatCredits(data.thisMonth)} <small>{t("common.credits")}</small></strong>
          {change === null ? (
            <p className="on-track-note">{t("pace.noComparison")}</p>
          ) : (
            <p className={`on-track-delta is-${direction}`}>
              {change > 0 ? "▲" : change < 0 ? "▼" : "—"} {Math.abs(change)}% {t("pace.deltaVs", { month: monthLabel(data.previousMonth) })}
              {" "}({formatCredits(data.lastMonth)})
            </p>
          )}
        </article>

        <article className="on-track-card">
          <span>{t("pace.usualPace")} <Explain of="pace" /></span>
          {data.averageBurn === null ? (
            <>
              <strong className="on-track-unknown">{t("pace.notEnoughHistory")}</strong>
              <p className="on-track-note">{t("pace.paceNeedsMonth")}</p>
            </>
          ) : (
            <>
              <strong>{formatCredits(data.averageBurn)} <small>{t("pace.creditsPerMonth")}</small></strong>
              <p className="on-track-note">
                {t("pace.averagedOver", { n: data.monthsObserved })}
              </p>
            </>
          )}
        </article>

        <article className="on-track-card">
          <span>{t("pace.byMonth", { month: monthLabel(data.contractEnd.slice(0, 7)) })} <Explain of="forecast" align="end" /></span>
          {data.projectedUnused === null ? (
            <>
              <strong className="on-track-unknown">{t("pace.noForecast")}</strong>
              <p className="on-track-note">{t("pace.monthsLeft", { n: data.remainingMonths })}</p>
            </>
          ) : data.projectedUnused > 0 ? (
            <>
              <strong>{formatCredits(data.projectedUnused)} <small>{t("pace.likelyUnused")}</small></strong>
              <p className="on-track-note">
                {t("pace.atRateAcross", { rate: formatCredits(data.averageBurn!), n: data.remainingMonths })}
                {" "}
                {data.monthsObserved === 1 ? t("pace.singleMonth") : t("pace.estimateNotCommitment")}
              </p>
            </>
          ) : (
            <>
              <strong className="on-track-tight">{t("pace.onPaceToUseAll")}</strong>
              <p className="on-track-note">{t("pace.nothingSpare", { rate: formatCredits(data.averageBurn!) })}</p>
            </>
          )}
        </article>
      </div>

      {data.affordable.length > 0 && (
        <div className="on-track-buys">
          <span>{t("pace.buysExample", { n: formatCredits(data.available) })}</span>
          <ul>
            {data.affordable.slice(0, 4).map((service) => (
              <li key={service.id}>
                <strong>{service.quantity}×</strong> {st.name(service.id, service.name)}
                <em>{t("common.crEach", { n: formatCredits(service.credits) })}</em>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
